import { ConsumerUnit } from 'entities/ConsumerUnit';

/**
 * Recebimento de faturas por e-mail — distribuidoras atendidas e status por
 * imóvel (configuração inicial, C1/C3 — out/2026).
 *
 * Só estas quatro têm leitura automática no backend (InboundEmailController):
 * o card dizia "Qualquer distribuidora" e um cliente da CEMIG/EDP configurava
 * tudo certo e nunca recebia nada. A lista muda junto com o roteamento do
 * webhook no backend.
 */
export interface InboundUtility {
  slug: string;
  label: string;
  site: string;
  siteLabel: string;
}

export const INBOUND_UTILITIES: InboundUtility[] = [
  {
    slug: 'cpfl_paulista',
    label: 'CPFL Paulista',
    site: 'https://www.cpfl.com.br',
    siteLabel: 'Abrir o site da CPFL',
  },
  {
    slug: 'elektro',
    label: 'Elektro / Neoenergia',
    site: 'https://www.neoenergia.com',
    siteLabel: 'Abrir o site da Neoenergia',
  },
  {
    slug: 'enel_sp',
    label: 'Enel SP',
    site: 'https://www.enel.com.br',
    siteLabel: 'Abrir o site da Enel',
  },
  {
    slug: 'light',
    label: 'Light',
    site: 'https://www.light.com.br',
    siteLabel: 'Abrir o site da Light',
  },
];

export const INBOUND_UTILITY_NAMES =
  'CPFL Paulista, Elektro/Neoenergia, Enel SP e Light';

export const inboundUtilityFor = (slug?: string): InboundUtility | undefined =>
  INBOUND_UTILITIES.find((u) => u.slug === slug);

/** Prazo para a 1ª fatura chegar depois do cadastro (um ciclo de faturamento). */
export const FIRST_INVOICE_DAYS = 30;
/** Sem fatura há mais que isto = algo parou (a conta é mensal). */
export const STALE_INVOICE_DAYS = 40;

export type InboundStatusKind =
  | 'receiving' // recebendo
  | 'stale' // sem fatura há 40+ dias
  | 'waiting' // cadastrou, aguardando a 1ª fatura
  | 'manual' // distribuidora sem leitura automática
  | 'todo'; // atendida, ainda não configurada

export interface InboundStatus {
  kind: InboundStatusKind;
  /** Dias desde a última fatura recebida por e-mail. */
  daysSinceLast?: number;
  /** Data limite esperada para a 1ª fatura (cadastro + 30 dias). */
  expectedBy?: Date;
}

const DAY = 24 * 60 * 60 * 1000;

export const inboundStatus = (
  uc: Pick<ConsumerUnit, 'lastInboundAt' | 'inboundSetupAt' | 'utilityCompany'>,
  now: Date = new Date()
): InboundStatus => {
  if (uc.lastInboundAt) {
    const daysSinceLast = Math.floor(
      (now.getTime() - new Date(uc.lastInboundAt).getTime()) / DAY
    );
    return {
      kind: daysSinceLast > STALE_INVOICE_DAYS ? 'stale' : 'receiving',
      daysSinceLast,
    };
  }
  if (!inboundUtilityFor(uc.utilityCompany?.slug)) return { kind: 'manual' };
  if (uc.inboundSetupAt) {
    return {
      kind: 'waiting',
      expectedBy: new Date(
        new Date(uc.inboundSetupAt).getTime() + FIRST_INVOICE_DAYS * DAY
      ),
    };
  }
  return { kind: 'todo' };
};
